import React, { useState, ReactNode, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface OverflowMenuItem {
  label: string
  icon?: ReactNode
  onClick?: () => void
  danger?: boolean
  checkable?: boolean
  checked?: boolean
  onToggle?: () => void
}

interface OverflowMenuProps {
  items: OverflowMenuItem[]
  className?: string
}

interface DropdownPos {
  top?: number
  bottom?: number
  right: number
  width: number
}

export function OverflowMenu({ items, className }: OverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return
    const handleClose = (e: MouseEvent) => {
      // Ignore clicks inside the trigger button or the portal dropdown
      if (buttonRef.current?.contains(e.target as Node)) return
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }

    // Close on window scroll/resizes but ignore scrolls that originate inside the dropdown
    const handleScroll = (ev: Event) => {
      try {
        const tgt = ev.target as Node | null
        if (dropdownRef.current && tgt && dropdownRef.current.contains(tgt)) {
          // scrolling inside the dropdown — do not close
          return
        }
      } catch (err) {
        // ignore
      }
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClose)
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true })
    window.addEventListener('resize', handleScroll)
    return () => {
      document.removeEventListener('mousedown', handleClose)
      window.removeEventListener('scroll', handleScroll, { capture: true })
      window.removeEventListener('resize', handleScroll)
    }
  }, [open])

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (open) { setOpen(false); return }
    if (!buttonRef.current) return
    const btnRect = buttonRef.current.getBoundingClientRect()
    const estimatedMenuHeight = Math.min(440, items.length * 44 + 8)
    const menuWidth = 176 // w-44 = 11rem = 176px
    const spaceBelow = window.innerHeight - btnRect.bottom - 8
    const openUpward = spaceBelow < estimatedMenuHeight && btnRect.top > estimatedMenuHeight
    const pos: DropdownPos = {
      right: window.innerWidth - btnRect.right,
      width: menuWidth,
    }
    if (openUpward) {
      pos.bottom = window.innerHeight - btnRect.top + 4
    } else {
      pos.top = btnRect.bottom + 4
    }
    setDropdownPos(pos)
    setOpen(true)
  }

  const dropdownStyle: React.CSSProperties = dropdownPos
    ? {
        position:  'fixed',
        top:       dropdownPos.top,
        bottom:    dropdownPos.bottom,
        right:     dropdownPos.right,
        width:     dropdownPos.width,
        // allow larger dropdowns up to viewport minus a small gap
        maxHeight: 'calc(100vh - 40px)',
        zIndex:    99999,
      }
    : {}

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        aria-label="Mais ações"
        aria-expanded={open}
      >
        <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v.01M12 12v.01M12 18v.01" />
        </svg>
      </button>

      {open && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg overflow-auto overscroll-contain"
          style={dropdownStyle}
        >
          <ul className="py-1 space-y-1 sm:space-y-0">
            {items.map((it, idx) => (
              <li key={idx}>
                {it.checkable ? (
                  <button
                    onClick={() => { it.onToggle?.(); setOpen(false) }}
                    className="w-full text-left px-3 py-3 sm:py-2 min-h-[44px] sm:min-h-0 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <input type="checkbox" checked={!!it.checked} readOnly className="w-4 h-4 pointer-events-none" />
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{it.label}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => { it.onClick?.(); setOpen(false) }}
                    className={`w-full text-left px-3 py-3 sm:py-2 min-h-[44px] sm:min-h-0 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700 ${it.danger ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'}`}
                  >
                    {it.icon && <span className="w-6 h-6 flex items-center justify-center">{it.icon}</span>}
                    <span className="flex-1 text-sm">{it.label}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  )
}

export default OverflowMenu
